import { Typography as MuiTypography } from '@mui/material'
import { Noto_Sans_JP, Noto_Serif_JP, Roboto, Roboto_Serif } from 'next/font/google'
import { type ReactNode } from 'react'
import { Colors } from './color'

export const noto = Noto_Sans_JP({
  weight: ['400', '500', '700'],
  subsets: ['latin'],
})

export const notoSerif = Noto_Serif_JP({
  weight: ['400', '500', '700'],
  subsets: ['latin'],
})

export const roboto = Roboto({
  weight: ['400', '500', '700'],
  subsets: ['latin'],
})

export const robotoSerif = Roboto_Serif({
  weight: ['400', '500', '700'],
  subsets: ['latin'],
})

const typographyVariants = {
  'Heading/32/Bold': {
    fontSize: '32px',
    fontWeight: '700',
    lineHeight: '40px',
    component: 'h1',
  },
  'Heading/24/Bold': {
    fontSize: '24px',
    fontWeight: '700',
    lineHeight: '32px',
    component: 'h1',
  },
  'Heading/20/Medium': {
    fontSize: '20px',
    fontWeight: '500',
    lineHeight: '28px',
    component: 'h2',
  },
  'Heading/20/Bold': {
    fontSize: '20px',
    fontWeight: '700',
    lineHeight: '28px',
    component: 'h2',
  },
  'Heading/18/Medium': {
    fontSize: '18px',
    fontWeight: '500',
    lineHeight: '26px',
    component: 'h3',
  },
  'Heading/18/Bold': {
    fontSize: '18px',
    fontWeight: '700',
    lineHeight: '26px',
    component: 'h3',
  },
  'Body/16/Regular': {
    fontSize: '16px',
    fontWeight: '400',
    lineHeight: '24px',
    component: 'span',
  },
  'Body/16/Medium': {
    fontSize: '16px',
    fontWeight: '500',
    lineHeight: '24px',
    component: 'span',
  },
  'Body/16/Bold': {
    fontSize: '16px',
    fontWeight: '700',
    lineHeight: '24px',
    component: 'span',
  },
  'Body/14/Regular': {
    fontSize: '14px',
    fontWeight: '400',
    lineHeight: '22px',
    component: 'span',
  },
  'Body/14/Medium': {
    fontSize: '14px',
    fontWeight: '500',
    lineHeight: '22px',
    component: 'span',
  },
  'Body/14/Bold': {
    fontSize: '14px',
    fontWeight: '700',
    lineHeight: '22px',
    component: 'span',
  },
  'Body/13/Regular': {
    fontSize: '13px',
    fontWeight: '400',
    lineHeight: '20px',
    component: 'span',
  },
  'Body/13/Medium': {
    fontSize: '13px',
    fontWeight: '500',
    lineHeight: '20px',
    component: 'span',
  },
  'Body/13/Bold': {
    fontSize: '13px',
    fontWeight: '700',
    lineHeight: '20px',
    component: 'span',
  },
  'Body/12/Regular': {
    fontSize: '12px',
    fontWeight: '400',
    lineHeight: '18px',
    component: 'span',
  },
  'Body/11/Regular': {
    fontSize: '11px',
    fontWeight: '400',
    lineHeight: '16px',
    component: 'span',
  },
  'Body/10/Regular': {
    fontSize: '10px',
    fontWeight: '400',
    lineHeight: '14px',
    component: 'span',
  },
  'Body/8/Regular': {
    fontSize: '8px',
    fontWeight: '400',
    lineHeight: '12px',
    component: 'span',
  },
} as const

type Props = {
  name?: keyof typeof typographyVariants
  color?: keyof typeof Colors
  serif?: boolean
  children: ReactNode
}

export const Typography = ({ name, color, serif, children }: Props) => {
  const { fontSize, fontWeight, lineHeight, component } =
    typographyVariants[name || 'Body/13/Regular']

  return (
    <MuiTypography
      fontSize={fontSize}
      fontWeight={fontWeight}
      lineHeight={lineHeight}
      component={component}
      color={color ? Colors[color] : undefined}
      fontFamily={
        serif
          ? `${robotoSerif.style.fontFamily}, ${notoSerif.style.fontFamily}`
          : `${roboto.style.fontFamily}, ${noto.style.fontFamily}`
      }
      sx={{ whiteSpace: 'pre-wrap' }}
    >
      {children}
    </MuiTypography>
  )
}
